import { Star, Quote, ExternalLink } from "lucide-react";
import { useContent } from "@/context/ContentContext";

export default function Testimonials() {
  const { content } = useContent();
  const reviews = content?.testimonials || [];
  const reviewUrl = (content?.contact?.review_url || "").trim();

  if (!reviews.length) return null;

  return (
    <section id="testimonials" data-testid="testimonials-section" className="py-24 md:py-32 bg-white border-y border-[#E5E7EB]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <div>
            <div className="font-display tracking-[0.3em] text-xs text-[#4AB300] mb-4">WHAT NEIGHBORS SAY</div>
            <h2 className="font-display uppercase text-4xl sm:text-5xl md:text-6xl leading-none tracking-tight">
              100%<br />
              <span className="text-[#4AB300]">Recommended</span>
            </h2>
          </div>
          <div className="flex items-center gap-3 text-[#374151]">
            <div className="flex gap-1">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} size={20} className="fill-[#FFD700] text-[#FFD700]" />
              ))}
            </div>
            <span className="font-display tracking-widest text-sm uppercase">
              Facebook Reviews · 100% Positive
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E5E7EB]">
          {reviews.map((r, i) => (
            <article
              key={r.id || i}
              data-testid={`testimonial-card-${i}`}
              className="bg-white p-8 flex flex-col relative hover:bg-[#EAEDF2] transition-colors"
            >
              <Quote size={36} className="text-[#4AB300]/40 mb-4" />
              <div className="flex gap-1 mb-4">
                {Array.from({ length: 5 }).map((_, j) => (
                  <Star key={j} size={14} className="fill-[#FFD700] text-[#FFD700]" />
                ))}
              </div>
              <p className="text-[#1F2937] leading-relaxed text-sm md:text-base flex-1">"{r.quote}"</p>
              <div className="mt-6 pt-6 border-t border-[#E5E7EB] flex items-center justify-between">
                <div>
                  <div className="font-display text-lg uppercase tracking-wide">{r.name}</div>
                  <div className="text-xs text-[#6B7280] tracking-widest uppercase mt-1">Facebook Review</div>
                </div>
                {r.tag && (
                  <span className="text-[10px] tracking-[0.2em] uppercase text-[#4AB300] border border-[#84FB00]/40 px-2 py-1">
                    {r.tag}
                  </span>
                )}
              </div>
            </article>
          ))}
        </div>

        {reviewUrl && (
          <div
            data-testid="google-review-cta"
            className="mt-14 border border-[#84FB00]/40 bg-gradient-to-br from-[#1F2328] via-white to-[#F5F7FA] p-8 md:p-12 flex flex-col md:flex-row md:items-center gap-6"
          >
            {/* Google G */}
            <svg width="56" height="56" viewBox="0 0 48 48" className="flex-shrink-0" aria-hidden="true">
              <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.1 8.1 3l5.7-5.7C34.9 6.1 29.7 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
              <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 15.7 18.9 12 24 12c3.1 0 5.9 1.1 8.1 3l5.7-5.7C34.9 6.1 29.7 4 24 4 16 4 9.1 8.7 6.3 14.7z"/>
              <path fill="#4CAF50" d="M24 44c5.7 0 10.7-2 14.3-5.2l-6.6-5.6C29.6 34.6 26.9 36 24 36c-5.2 0-9.6-3.3-11.2-7.9L6.2 33C9 39.1 16 44 24 44z"/>
              <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4 5.6l6.6 5.6C41.8 36.8 44 30.9 44 24c0-1.3-.1-2.4-.4-3.5z"/>
            </svg>
            <div className="flex-1">
              <div className="font-display uppercase text-2xl md:text-3xl tracking-tight leading-tight">
                Loved working with Hondo?
              </div>
              <div className="text-[#4B5563] mt-2 text-sm md:text-base leading-relaxed">
                Take 30 seconds and drop a review on Google — it means the world to a small business and helps your neighbors find us.
              </div>
            </div>
            <a
              href={reviewUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="google-review-cta-button"
              className="inline-flex items-center gap-3 bg-[#84FB00] hover:bg-[#6EE500] active:bg-[#5AC900] transition-colors px-8 py-4 font-display tracking-widest uppercase text-[#0A0E1A] text-sm md:text-base whitespace-nowrap"
            >
              Leave a Google Review
              <ExternalLink size={16} />
            </a>
          </div>
        )}
      </div>
    </section>
  );
}
